import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Zap, Target, TrendingUp, ArrowRight } from 'lucide-react';
import Button from '../components/ui/Button';
import LoadingState from '../components/ui/LoadingState';
import { useAuth } from '../contexts/AuthContext';

const WhopSuccess = () => {
  const navigate = useNavigate();
  const { user, profile, loading } = useAuth();
  const [newFeatures, setNewFeatures] = useState([]);

  useEffect(() => {
    // If not authenticated, show them instructions to check email
    if (!loading && !user) {
      // Stay on this page - we'll show instructions to check email for password setup
      return;
    }

    // Redirect to onboarding if not completed
    if (!loading && user && profile) {
      if (!profile.onboarding_completed || !profile.industry) {
        navigate('/app/industry-selection');
        return;
      }
    }

    const tier = profile?.subscription_tier || profile?.subscription_plan;

    // Determine which features were unlocked based on subscription tier
    if (tier === 'pro') {
      setNewFeatures([
        { icon: <Target className="w-5 h-5" />, name: 'All Training Modules', description: 'Access complete library' },
        { icon: <Zap className="w-5 h-5" />, name: 'Objection Handling Drills', description: 'Master every objection' },
        { icon: <TrendingUp className="w-5 h-5" />, name: 'Script Library', description: 'Proven scripts & templates' },
        { icon: <CheckCircle className="w-5 h-5" />, name: 'Performance Analytics', description: 'Track your progress' }
      ]);
    } else if (tier === 'elite') {
      setNewFeatures([
        { icon: <Target className="w-5 h-5" />, name: 'Everything in Pro', description: 'Full feature access' },
        { icon: <Zap className="w-5 h-5" />, name: 'AI Conversation Analyzer', description: 'Analyze sales calls with AI' },
        { icon: <TrendingUp className="w-5 h-5" />, name: 'AI Roleplay Scenarios', description: 'Practice with AI prospects' },
        { icon: <CheckCircle className="w-5 h-5" />, name: 'Advanced Feedback Engine', description: 'Detailed coaching insights' }
      ]);
    }
  }, [profile, loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingState message="Loading your subscription details..." />
      </div>
    );
  }

  // If not authenticated, show email verification instructions
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-primary" />
              </div>
            </div>
            
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-3">Payment Successful!</h1>
              <p className="text-muted-foreground mb-4">
                Your account has been created. Check your email to set your password and get started.
              </p>
            </div>
            
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-semibold text-foreground mb-2">Next Steps:</p>
              <ol className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">1.</span>
                  <span>Check your email inbox (and spam folder)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">2.</span>
                  <span>Click the password setup link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">3.</span>
                  <span>Create your password and start training!</span>
                </li>
              </ol>
            </div>
            
            <Button
              fullWidth
              variant="outline"
              onClick={() => navigate('/authentication')}
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <div className="bg-card rounded-2xl shadow-xl p-8 md:p-12 border border-border">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-primary" />
            </div>
          </div>

          {/* Success Message */}
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold mb-3">
                Welcome to {(profile?.subscription_tier || profile?.subscription_plan) === 'elite' ? 'Elite' : 'Pro'}!
                </h1>
            <p className="text-lg text-muted-foreground">
              Your subscription is now active. You've unlocked powerful new features to accelerate your sales growth.
            </p>
          </div>

          {/* New Features List */}
          {newFeatures.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-center">🎉 New Features Unlocked</h2>
              <div className="grid gap-4">
                {newFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border hover:border-primary/50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 text-primary">
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">{feature.name}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="space-y-3">
            <Button
              size="lg"
              className="w-full"
              onClick={() => navigate('/app/dashboard')}
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full"
              onClick={() => navigate('/app/daily-drill')}
            >
              Start Your First Drill
            </Button>
          </div>

          {/* Additional Info */}
          <div className="mt-6 pt-6 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              Check your navigation menu to explore all your new features. Need help getting started?{' '}
              <a href="/app/help" className="text-primary hover:underline">
                Visit our help center
              </a>
            </p>
          </div>
        </div>

        {/* Quick Tip */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            💡 <strong>Pro Tip:</strong> Start with the daily drill to build your streak and earn XP!
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhopSuccess;
